import { useRef, useState, useEffect } from "react";

const VideoMarkers = ({ timeStamps, controllerRef }) => {
  const [player, setPlayer] = useState(null);

  // Array of markers in seconds (converted from your timestamp format)
  const markers = timeStamps; // [00:10, 01:00, 01:04] converted to seconds

  useEffect(() => {
    if (controllerRef.current) {
      setPlayer(controllerRef.current);
    }
  }, []);

  useEffect(() => {
    // Try multiple times with increasing delays to ensure Shaka UI is ready
    const attempts = [500, 1000, 2000, 3000];
    const timers = attempts.map((delay) =>
      setTimeout(() => addMarkersToProgressBar(), delay)
    );

    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [player, markers]);

  const addMarkersToProgressBar = () => {
    if (!controllerRef.current) return;

    const videoElement =
      controllerRef.current.videoElement || controllerRef.current;

    const handleLoadedMetadata = () => {
      const duration = videoElement.duration;
      if (!duration || duration === 0) {
        setTimeout(handleLoadedMetadata, 500);
        return;
      }

      // Remove existing markers first
      document
        .querySelectorAll(".video-marker")
        .forEach((marker) => marker.remove());

      // Extended list of selectors for Shaka Player seek bar
      let seekBar = null;
      const seekBarSelectors = [
        // Shaka Player specific selectors
        ".shaka-seek-bar",
        ".shaka-seek-bar-container",
        ".shaka-progress-container",
        ".shaka-progress-bar",
        ".shaka-controls-container .shaka-seek-bar",
        ".shaka-controls-container .shaka-range-container",
        '.shaka-controls-container input[type="range"]',
        // Generic selectors
        '[class*="shaka"][class*="seek"]',
        '[class*="shaka"][class*="progress"]',
        '[role="progressbar"]',
        'input[type="range"]',
        '.video-container [class*="seek"]',
        '.video-container [class*="progress"]',
      ];

      // Try each selector globally first
      for (const selector of seekBarSelectors) {
        seekBar = document.querySelector(selector);
        if (seekBar) {
          break;
        }
      }

      // If not found, try within video container
      if (!seekBar) {
        const videoContainer =
          controllerRef.current.closest(".video-container") ||
          controllerRef.current.parentElement;
        if (videoContainer) {
          for (const selector of seekBarSelectors) {
            seekBar = videoContainer.querySelector(selector);
            if (seekBar) {
              
              break;
            }
          }
        }
      }

      if (seekBar) {
        // Make sure the seek bar can contain absolute positioned elements
        const computedStyle = window.getComputedStyle(seekBar);
        if (computedStyle.position === "static") {
          seekBar.style.position = "relative";
        }

        // Add markers to the existing seek bar
        markers.forEach((timestamp, index) => {
          if (timestamp <= duration) {
            const marker = document.createElement("div");
            marker.className = "video-marker";
            marker.setAttribute("data-timestamp", timestamp);
            marker.style.cssText = `
              position: absolute;
              top: 0;
              bottom: 0;
              width: 3px;
              background-color: #ff0000 !important;
              left: ${(timestamp / duration) * 100}%;
              z-index: 999;
              pointer-events: none;
              border-radius: 1px;
            `;
            seekBar.appendChild(marker);
          }
        });
      } else {
        
        // Keep retrying
        setTimeout(handleLoadedMetadata, 1000);
      }
    };

    if (videoElement && videoElement.readyState >= 1) {
      handleLoadedMetadata();
    } else if (videoElement) {
      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata, {
        once: true,
      });
      videoElement.addEventListener("canplay", handleLoadedMetadata, {
        once: true,
      });
      videoElement.addEventListener("durationchange", handleLoadedMetadata, {
        once: true,
      });
    }
  };

  // Fallback method - render simple markers if seek bar injection fails
  const videoElement =
    controllerRef.current?.videoElement || controllerRef.current;
  if (!videoElement || !videoElement.duration || videoElement.duration === 0)
    return null;

  return (
    <div
      className="fallback-markers"
      style={{
        position: "absolute",
        bottom: "26px", // Position above controls
        left: "12px",
        right: "12px",
        height: "4px",
        pointerEvents: "none",
        zIndex: 1000,
      }}
    >
      {markers.map((timestamp, index) => (
        <div
          key={index}
          className="fallback-marker"
          style={{
            position: "absolute",
            top: "0",
            width: "3px",
            height: "4px",
            backgroundColor: "#ff0000",
            left: `${(timestamp / videoElement.duration) * 100}%`,
            borderRadius: "1px",
          }}
        />
      ))}
    </div>
  );
};

export default VideoMarkers;
